import type { AppResource, CreateAppRequest, ExperimentResource, StoreDesignResource } from '@studio/shared';
import { counted } from '../inventory/model.js';

/** What an App looks like when the Workspace is deciding what to show first. */
export interface AppStanding {
  readonly app: AppResource;
  readonly activeJobs: number;
  readonly activeExperiments: number;
  readonly closedExperiments: number;
  readonly openDesigns: number;
}

/** An Experiment is still live work until it is closed. */
export function isExperimentActive(experiment: ExperimentResource): boolean {
  return experiment.state !== 'closed';
}

/** Experiments owned by a Store Design are shown under Оформления, not as free Experiments. */
export function standAloneExperiments(
  experiments: readonly ExperimentResource[],
  designs: readonly StoreDesignResource[],
): readonly ExperimentResource[] {
  const grouped = new Set(designs.flatMap((design) => [design.iconExperimentId, design.screenshotsExperimentId]));
  return experiments.filter((experiment) => !grouped.has(experiment.id));
}

/**
 * Apps with work in flight first. Work in flight is a Job the operator is waiting on,
 * then an Experiment that has not been closed; equally quiet Apps fall back to the one
 * touched most recently, so the order never depends on insertion order.
 */
export function standings(
  apps: readonly AppResource[],
  experiments: readonly ExperimentResource[],
  activeJobAppIds: readonly (string | null)[],
  designs: readonly StoreDesignResource[] = [],
): readonly AppStanding[] {
  const ranked = apps.map((app) => {
    const mine = experiments.filter((experiment) => experiment.appId === app.id);
    return {
      app,
      activeJobs: activeJobAppIds.filter((appId) => appId === app.id).length,
      activeExperiments: mine.filter(isExperimentActive).length,
      closedExperiments: mine.filter((experiment) => !isExperimentActive(experiment)).length,
      openDesigns: designs.filter((design) => design.appId === app.id).length,
    };
  });

  return [...ranked].sort((a, b) => {
    if (a.activeJobs !== b.activeJobs) return b.activeJobs - a.activeJobs;
    if (a.activeExperiments !== b.activeExperiments) return b.activeExperiments - a.activeExperiments;
    if (a.openDesigns !== b.openDesigns) return b.openDesigns - a.openDesigns;
    return b.app.updatedAt.localeCompare(a.app.updatedAt);
  });
}

export function countLine(standing: AppStanding): string {
  const active = counted(standing.activeExperiments, 'активный Experiment', 'активных Experiment', 'активных Experiment');
  const closed = counted(standing.closedExperiments, 'закрытый', 'закрытых', 'закрытых');
  const designs =
    standing.openDesigns === 0 ? null : counted(standing.openDesigns, 'оформление', 'оформления', 'оформлений');
  return designs === null ? `${active} · ${closed}` : `${active} · ${closed} · ${designs}`;
}

/** A package name, as Google Play writes one. */
const PACKAGE_LIKE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i;

/**
 * One field, two meanings. A listing address or a package name is something the Studio
 * can go and read; anything else is simply the name of an App that has nothing on Play
 * yet, and neither of them is a setup screen.
 */
export function appRequestFor(entered: string): CreateAppRequest {
  const text = entered.trim();
  if (text.includes('://') || text.toLowerCase().includes('play.google.com') || PACKAGE_LIKE.test(text)) {
    return { paste: text };
  }
  return { displayName: text };
}
